import * as T from 'styled/components/Typography';

import React, { useEffect } from 'react';
import { selectMatchStatisticsAdmin } from 'features/match/matchSlice';
import { patchMatchStatistics, postMatchStatistics } from 'features/match/thunks';
import { selectChoose, statsSliceActions } from '../../features/stats/statsSlice';
import { useSelector } from 'react-redux';

import AddIcon from 'assets/images/icons/add.svg';
import { DynamicFieldProps } from './StatsTypes';
import LoadingSpinner from '../../atoms/LoadingSpinner';
import { MatchStatisticStatus } from '../../enums/matchStatisticStatus';
import { StyledDynamicField } from './StyledStats';
import SubtractIcon from 'assets/images/icons/subtract.svg';
import { TeamOption } from '../../enums/teamOption';
import classNames from 'classnames';
import lodash from 'lodash';
import { MatchStatisticType } from '../../enums/matchStatisticType';
import { useAppDispatch } from '../../hooks/hooks';

const BLINK_TIMEOUT = 4000;

const DynamicField: React.FC<DynamicFieldProps> = ({ label, statsSelect, statField, disabled }) => {
  const dispatch = useAppDispatch();
  const choose = useSelector(selectChoose);

  const { auto, name, matchStatisticsTypeId } = statField;

  const { homeCount, awayCount } = statsSelect.data;
  const { homeTeamId, awayTeamId } = statsSelect.teamIds;
  const { homeId, awayId } = statsSelect.ids;
  const matchId = statsSelect.matchId;

  const matchStatistics = useSelector(selectMatchStatisticsAdmin);
  const matchStatisticHome = matchStatistics?.find((matchStatistic) => matchStatistic.id === homeId);
  const matchStatisticAway = matchStatistics?.find((matchStatistic) => matchStatistic.id === awayId);

  const loadingHome =
    matchStatisticHome &&
    [MatchStatisticStatus.POSTING, MatchStatisticStatus.PATCHING].includes(matchStatisticHome.status);
  const loadingAway =
    matchStatisticAway &&
    [MatchStatisticStatus.POSTING, MatchStatisticStatus.PATCHING].includes(matchStatisticAway.status);

  const doChoose = choose[name === 'totalShots' ? 'shotsOffTarget' : name];

  const homeSubtractDisabled = disabled || homeCount === 0;
  const awaySubtractDisabled = disabled || awayCount === 0;

  useEffect(() => {
    let timer;
    let timer2;

    if (doChoose !== undefined) {
      timer2 = setTimeout(() => {
        dispatch(statsSliceActions.removeChoose(name === 'totalShots' ? 'shotsOffTarget' : name));
      }, BLINK_TIMEOUT);
    }
    return () => {
      clearTimeout(timer);
      clearTimeout(timer2);
    };
  });

  const doIncrementOrPost = (teamId: number, matchStatisticsTypeId: number, value: number) => {
    let found = false;
    matchStatistics?.forEach((matchStatistic) => {
      if (
        !found &&
        matchStatistic.matchStatisticsTypeId === matchStatisticsTypeId &&
        matchStatistic.teamId === teamId
      ) {
        found = true;
        if (value !== 0) {
          const matchStatisticOther = lodash.cloneDeep(matchStatistic);
          matchStatisticOther.statisticsCount++;
          dispatch(patchMatchStatistics(matchStatisticOther));
        }
      }
    });
    if (!found) {
      const matchStatistic = {
        teamId: teamId,
        matchId: matchId,
        statisticsCount: value,
        matchStatisticsTypeId: matchStatisticsTypeId,
        status: MatchStatisticStatus.POSTING,
      };
      dispatch(postMatchStatistics(matchStatistic));
    }
  };

  const doDecrement = (teamId: number, matchStatisticsTypeId: number) => {
    let found = false;
    matchStatistics?.forEach((matchStatistic) => {
      if (
        !found &&
        matchStatistic.matchStatisticsTypeId === matchStatisticsTypeId &&
        matchStatistic.teamId === teamId
      ) {
        found = true;
        if (matchStatistic.statisticsCount - 1 >= 0) {
          const matchStatisticOther = lodash.cloneDeep(matchStatistic);
          matchStatisticOther.statisticsCount--;
          dispatch(patchMatchStatistics(matchStatisticOther));
        }
      }
    });
  };

  const increment = (value: number, teamOption: TeamOption, matchStatisticId?: number) => {
    const firstTeamId = teamOption === TeamOption.HOME ? homeTeamId : awayTeamId;
    const secondTeamId = teamOption === TeamOption.HOME ? awayTeamId : homeTeamId;

    if (matchStatisticId) {
      doIncrementOrPost(firstTeamId, matchStatisticsTypeId, 1);

      if (name === 'postHits') {
        doIncrementOrPost(firstTeamId, MatchStatisticType.CHANCES, 1);
        doIncrementOrPost(secondTeamId, MatchStatisticType.CHANCES, 0);
      }

      if (['shotsOnGoal', 'postHits', 'shotsOffTarget'].includes(name)) {
        doIncrementOrPost(firstTeamId, MatchStatisticType.TOTAL_SHOTS, 1);
        doIncrementOrPost(secondTeamId, MatchStatisticType.TOTAL_SHOTS, 0);
      }
    } else {
      doIncrementOrPost(firstTeamId, matchStatisticsTypeId, 1);
      doIncrementOrPost(secondTeamId, matchStatisticsTypeId, 0);

      if (name === 'postHits') {
        doIncrementOrPost(firstTeamId, MatchStatisticType.CHANCES, 1);
        doIncrementOrPost(secondTeamId, MatchStatisticType.CHANCES, 0);
      }

      if (['shotsOnGoal', 'postHits', 'shotsOffTarget'].includes(name)) {
        doIncrementOrPost(firstTeamId, MatchStatisticType.TOTAL_SHOTS, 1);
        doIncrementOrPost(secondTeamId, MatchStatisticType.TOTAL_SHOTS, 0);
      }
    }
  };

  const decrement = (value: number, teamOption: TeamOption) => {
    const firstTeamId = teamOption === TeamOption.HOME ? homeTeamId : awayTeamId;

    doDecrement(firstTeamId, matchStatisticsTypeId);

    if (name === 'postHits') {
      doDecrement(firstTeamId, MatchStatisticType.CHANCES);
    }

    if (['shotsOnGoal', 'postHits', 'shotsOffTarget'].includes(name)) {
      doDecrement(firstTeamId, MatchStatisticType.TOTAL_SHOTS);
    }
  };

  return (
    <StyledDynamicField className={classNames({ auto })}>
      <div className="home">
        {loadingHome && <LoadingSpinner loading={true} />}
        {!loadingHome && (
          <div className="manual">
            <img
              src={SubtractIcon}
              className={'subtract ' + (homeSubtractDisabled ? 'disabled' : '')}
              onClick={disabled === true ? undefined : () => decrement(homeCount, TeamOption.HOME)}
              alt="subtract"
            />
            <div className={'value ' + (doChoose === 'home' ? 'alert-choose' : '')}>{homeCount}</div>
            <img
              src={AddIcon}
              className={'add ' + (disabled ? 'disabled' : '')}
              onClick={disabled === true ? undefined : () => increment(homeCount, TeamOption.HOME, homeId)}
              alt="add"
            />
          </div>
        )}
      </div>
      <div className="middle">
        <T.Copy>{label}</T.Copy>
      </div>
      <div className="away">
        {loadingAway && <LoadingSpinner loading={true} />}
        {!loadingAway && (
          <div className="manual">
            <img
              src={AddIcon}
              className={'add ' + (disabled ? 'disabled' : '')}
              onClick={disabled === true ? undefined : () => increment(awayCount, TeamOption.AWAY, awayId)}
              alt="add"
            />
            <div className={'value ' + (doChoose === 'away' ? 'alert-choose' : '')}>{awayCount}</div>
            <img
              src={SubtractIcon}
              className={'subtract ' + (awaySubtractDisabled ? 'disabled' : '')}
              onClick={disabled === true ? undefined : () => decrement(awayCount, TeamOption.AWAY)}
              alt="subtract"
            />
          </div>
        )}
      </div>
    </StyledDynamicField>
  );
};

export default DynamicField;
